import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Search, ArrowRight, Clock, Calendar, CircleCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import BlogHeroArt from '../components/BlogHeroArt';
import { tokens } from '../theme';
import { blogPosts, getFeaturedPost, getAllCategories } from '../data/blogPosts';

const API_BASE = 'https://cleolead-api-743703226990.us-central1.run.app';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const [searchQuery, setSearchQuery] = React.useState('');

  // Newsletter subscribe state
  const [subscribeEmail, setSubscribeEmail] = React.useState('');
  const [subscribing, setSubscribing] = React.useState(false);
  const [subscribed, setSubscribed] = React.useState(false);
  const [subscribeError, setSubscribeError] = React.useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = subscribeEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubscribeError('Please enter a valid email address.');
      return;
    }
    setSubscribing(true);
    setSubscribeError('');
    try {
      const response = await fetch(`${API_BASE}/api/v1/public/blog-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : 'Could not subscribe right now. Please try again.',
        );
      }
      setSubscribed(true);
      setSubscribeEmail('');
    } catch (err) {
      setSubscribeError(
        err instanceof Error ? err.message : 'Could not subscribe right now. Please try again.',
      );
    } finally {
      setSubscribing(false);
    }
  };

  const featuredPost = getFeaturedPost();
  const categories = ['All', ...getAllCategories()];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && post.slug !== featuredPost?.slug;
  });

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 6 }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" component="p" sx={{ mb: 2 }}>
              Blog
            </Typography>
            <Typography variant="h2" component="h1" gutterBottom>
              Insights & Resources
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto', mb: 4, fontSize: '1.125rem' }}
            >
              Research-backed playbooks for running your front office with AI — sales, support, voice, calendar, and the back-office work behind them.
            </Typography>
            <TextField
              placeholder="Search articles..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: '100%',
                maxWidth: 500,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: `${tokens.radiusControl}px`,
                  '& fieldset': {
                    borderColor: tokens.border,
                  },
                  '&:hover fieldset': {
                    borderColor: tokens.copper,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: tokens.copper,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} strokeWidth={1.5} color={tokens.textSecondary} />
                  </InputAdornment>
                ),
              }}
            />
          </Reveal>
        </Container>
      </Box>

      {/* Categories */}
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 6, justifyContent: 'center' }}>
          {categories.map((category) => {
            const active = selectedCategory === category;
            return (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '999px',
                  fontWeight: active ? 600 : 500,
                  bgcolor: active ? tokens.copperTint : 'background.paper',
                  color: active ? tokens.copperDark : 'text.secondary',
                  border: active
                    ? '1px solid rgba(199, 91, 57, 0.35)'
                    : `1px solid ${tokens.border}`,
                  transition: `border-color ${tokens.motion.micro}ms ease, color ${tokens.motion.micro}ms ease, background-color ${tokens.motion.micro}ms ease`,
                  '&:hover': {
                    bgcolor: active ? tokens.copperTintStrong : 'background.paper',
                    borderColor: tokens.copper,
                    color: active ? tokens.copperDark : tokens.copper,
                  },
                }}
              />
            );
          })}
        </Box>
      </Container>

      {/* Featured Post */}
      {featuredPost && selectedCategory === 'All' && searchQuery === '' && (
        <Box sx={{ pb: 8 }}>
          <Container maxWidth="lg">
            <Reveal>
              <Card
                onClick={() => navigate(`/blog/${featuredPost.slug}`)}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  cursor: 'pointer',
                  boxShadow: tokens.shadowLift,
                  transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                  '&:hover': {
                    borderColor: '#D9CDBC',
                    boxShadow: tokens.shadowLift,
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', md: '50%' },
                    minHeight: { xs: 250, md: 350 },
                    borderRight: { md: `1px solid ${tokens.border}` },
                    borderBottom: { xs: `1px solid ${tokens.border}`, md: 'none' },
                    display: 'flex',
                    '& > *': { flex: 1 },
                  }}
                >
                  <BlogHeroArt
                    slug={featuredPost.slug}
                    title={featuredPost.title}
                    height="100%"
                  />
                </Box>
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Chip
                    label={featuredPost.category}
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      mb: 2,
                      bgcolor: tokens.copperTint,
                      color: tokens.copperDark,
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="h3" component="h2" gutterBottom>
                    {featuredPost.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {featuredPost.excerpt}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Calendar size={14} strokeWidth={1.5} color={tokens.textSecondary} />
                      <Typography variant="body2" color="text.secondary">
                        {featuredPost.date}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Clock size={14} strokeWidth={1.5} color={tokens.textSecondary} />
                      <Typography variant="body2" color="text.secondary">
                        {featuredPost.readTime}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    endIcon={<ArrowRight size={18} strokeWidth={1.5} />}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Read Article
                  </Button>
                </CardContent>
              </Card>
            </Reveal>
          </Container>
        </Box>
      )}

      {/* Blog Posts Grid */}
      <Box
        sx={{
          py: 8,
          bgcolor: tokens.bandAlt,
          borderTop: `1px solid ${tokens.border}`,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom sx={{ mb: 4 }}>
            {selectedCategory === 'All' ? 'Latest Articles' : selectedCategory}
          </Typography>
          {filteredPosts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No articles found matching your criteria.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {filteredPosts.map((post, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.slug}>
                  <Reveal
                    delay={(index % 3) * tokens.motion.stagger}
                    sx={{ height: '100%' }}
                  >
                    <Card
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                        '&:hover': {
                          borderColor: '#D9CDBC',
                          boxShadow: tokens.shadowLift,
                        },
                      }}
                    >
                      <Box sx={{ borderBottom: `1px solid ${tokens.border}` }}>
                        <BlogHeroArt slug={post.slug} title={post.title} height={140} />
                      </Box>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                        <Chip
                          label={post.category}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            mb: 1.5,
                            bgcolor: tokens.copperTint,
                            color: tokens.copperDark,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }} gutterBottom>
                          {post.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
                          {post.excerpt}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Calendar size={13} strokeWidth={1.5} color={tokens.textSecondary} />
                            <Typography variant="caption" color="text.secondary">
                              {post.date}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Clock size={13} strokeWidth={1.5} color={tokens.textSecondary} />
                            <Typography variant="caption" color="text.secondary">
                              {post.readTime}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Newsletter */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="sm">
          <Reveal>
            <Box
              sx={{
                textAlign: 'center',
                p: { xs: 4, md: 5 },
                borderRadius: `${tokens.radiusCard}px`,
                bgcolor: 'background.paper',
                border: `1px solid ${tokens.border}`,
                boxShadow: tokens.shadowSoft,
              }}
            >
              <Typography variant="h3" component="h2" gutterBottom>
                Subscribe to Our Newsletter
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                New playbooks for AI-run sales, support, and operations — straight to your inbox.
              </Typography>
              {subscribed ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ color: tokens.sage, display: 'inline-flex', mb: 1 }}>
                    <CircleCheck size={44} strokeWidth={1.5} />
                  </Box>
                  <Typography sx={{ fontWeight: 600 }}>You're subscribed!</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check your inbox for a confirmation email.
                  </Typography>
                </Box>
              ) : (
                <>
                  {subscribeError && (
                    <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                      {subscribeError}
                    </Alert>
                  )}
                  <Box
                    component="form"
                    onSubmit={handleSubscribe}
                    sx={{ display: 'flex', gap: 1, mt: 3 }}
                  >
                    <TextField
                      placeholder="Enter your email"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="email"
                      value={subscribeEmail}
                      onChange={(e) => {
                        setSubscribeEmail(e.target.value);
                        setSubscribeError('');
                      }}
                      disabled={subscribing}
                      inputProps={{ 'aria-label': 'Email address' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: tokens.bgDefault,
                          borderRadius: `${tokens.radiusControl}px`,
                          '& fieldset': {
                            borderColor: tokens.border,
                          },
                          '&:hover fieldset': {
                            borderColor: tokens.copper,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: tokens.copper,
                          },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={subscribing}
                      startIcon={
                        subscribing ? <CircularProgress size={16} color="inherit" /> : undefined
                      }
                      sx={{ whiteSpace: 'nowrap', px: 3 }}
                    >
                      {subscribing ? 'Subscribing…' : 'Subscribe'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Reveal>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default BlogPage;
